import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CssBaseline, ThemeProvider } from '@mui/material';
import {
  createAppTheme,
  resolveThemeMode,
  THEME_MODE_STORAGE_KEY,
  THEME_MODES,
} from '../theme';

const ThemeModeContext = createContext({
  mode: THEME_MODES.LIGHT,
  isDarkMode: false,
  toggleThemeMode: () => {},
  setThemeMode: () => {},
});

const getInitialMode = () => {
  if (typeof window === 'undefined') {
    return THEME_MODES.LIGHT;
  }
  const storedMode = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);
  if (storedMode) {
    return resolveThemeMode(storedMode);
  }
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
  return prefersDark ? THEME_MODES.DARK : THEME_MODES.LIGHT;
};

export const ThemeModeProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialMode);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const resolvedMode = resolveThemeMode(mode);
    window.localStorage.setItem(THEME_MODE_STORAGE_KEY, resolvedMode);
    document.documentElement.setAttribute('data-theme-mode', resolvedMode);
  }, [mode]);

  const setThemeMode = useCallback((nextMode) => {
    setMode(resolveThemeMode(nextMode));
  }, []);

  const toggleThemeMode = useCallback(() => {
    setMode((prevMode) => (prevMode === THEME_MODES.DARK ? THEME_MODES.LIGHT : THEME_MODES.DARK));
  }, []);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const contextValue = useMemo(
    () => ({
      mode,
      isDarkMode: mode === THEME_MODES.DARK,
      toggleThemeMode,
      setThemeMode,
    }),
    [mode, setThemeMode, toggleThemeMode]
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

ThemeModeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeMode = () => useContext(ThemeModeContext);

export default ThemeModeContext;

